import type {
  Insight,
  InsightLookupByKey,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { InsightKey } from "@tandemdiabetes/sugarmate-api-react-client"
import type { FC } from "react"
import { useMemo } from "react"

import { defaultInsightRangeByKey } from "../defaultInsightRangeByKey"
import { ExerciseAverageDuration } from "./ExerciseAverageDuration"
import { ExerciseTotalDuration } from "./ExerciseTotalDuration"
import { FoodAverageCalories } from "./FoodAverageCalories"
import { FoodAverageCarbs } from "./FoodAverageCarbs"
import { FoodAverageFat } from "./FoodAverageFat"
import { FoodAverageFiber } from "./FoodAverageFiber"
import { FoodAverageProtein } from "./FoodAverageProtein"
import { FoodTotalCalories } from "./FoodTotalCalories"
import { FoodTotalCarbs } from "./FoodTotalCarbs"
import { FoodTotalFat } from "./FoodTotalFat"
import { FoodTotalFiber } from "./FoodTotalFiber"
import { FoodTotalProtein } from "./FoodTotalProtein"
import { GlucoseAverageReading } from "./GlucoseAverageReading"
import { GlucoseAverageReadingBedtime } from "./GlucoseAverageReadingBedtime"
import { GlucoseAverageReadingWakeUp } from "./GlucoseAverageReadingWakeUp"
import { GlucoseCoefficientOfVariation } from "./GlucoseCoefficientOfVariation"
import { GlucoseFluxGrade } from "./GlucoseFluxGrade"
import { GlucoseHighsAndLows } from "./GlucoseHighsAndLows"
import { GlucoseManagementIndicator } from "./GlucoseManagementIndicator"
import { GlucoseMedianReading } from "./GlucoseMedianReading"
import { GlucosePercentageAbove } from "./GlucosePercentageAbove"
import { GlucosePercentageBelow } from "./GlucosePercentageBelow"
import { GlucosePercentageDistribution } from "./GlucosePercentageDistribution"
import { GlucosePercentageWithin } from "./GlucosePercentageWithin"
import { GlucoseQuartiles } from "./GlucoseQuartiles"
import { GlucoseStandardDeviation } from "./GlucoseStandardDeviation"
import { GlucoseTotalUnicorns } from "./GlucoseTotalUnicorns"
import { InsulinAverage } from "./InsulinAverage"
import { InsulinAverageBasal } from "./InsulinAverageBasal"
import { InsulinAverageBolus } from "./InsulinAverageBolus"
import { InsulinTotal } from "./InsulinTotal"
import { InsulinTotalBasal } from "./InsulinTotalBasal"
import { InsulinTotalBolus } from "./InsulinTotalBolus"
import { LiquidAverage } from "./LiquidAverage"
import { LiquidTotal } from "./LiquidTotal"
import { MiniGraph } from "./MiniGraph"
import type { TileProps } from "./TileProps"
import { TimeInRange } from "./TimeInRange"

interface Props {
  insight: Insight;
}

export function TileChildren(props: Props) {
  const { insight } = props

  const insightKey = insight.key

  const TileComponent = useMemo(
    () => {
      let value = null

      switch (insightKey) {
        case InsightKey.ExerciseAverageDuration:
          value = ExerciseAverageDuration
          break
        case InsightKey.ExerciseTotalDuration:
          value = ExerciseTotalDuration
          break
        case InsightKey.FoodAverageCalories:
          value = FoodAverageCalories
          break
        case InsightKey.FoodAverageCarbs:
          value = FoodAverageCarbs
          break
        case InsightKey.FoodAverageFat:
          value = FoodAverageFat
          break
        case InsightKey.FoodAverageFiber:
          value = FoodAverageFiber
          break
        case InsightKey.FoodAverageProtein:
          value = FoodAverageProtein
          break
        case InsightKey.FoodTotalCalories:
          value = FoodTotalCalories
          break
        case InsightKey.FoodTotalCarbs:
          value = FoodTotalCarbs
          break
        case InsightKey.FoodTotalFat:
          value = FoodTotalFat
          break
        case InsightKey.FoodTotalFiber:
          value = FoodTotalFiber
          break
        case InsightKey.FoodTotalProtein:
          value = FoodTotalProtein
          break
        case InsightKey.GlucoseAverageReading:
          value = GlucoseAverageReading
          break
        case InsightKey.GlucoseAverageReadingBedtime:
          value = GlucoseAverageReadingBedtime
          break
        case InsightKey.GlucoseAverageReadingWakeUp:
          value = GlucoseAverageReadingWakeUp
          break
        case InsightKey.GlucoseCoefficientOfVariation:
          value = GlucoseCoefficientOfVariation
          break
        case InsightKey.GlucoseFluxGrade:
          value = GlucoseFluxGrade
          break
        case InsightKey.GlucoseHighsAndLows:
          value = GlucoseHighsAndLows
          break
        case InsightKey.GlucoseManagementIndicator:
          value = GlucoseManagementIndicator
          break
        case InsightKey.GlucoseMedianReading:
          value = GlucoseMedianReading
          break
        case InsightKey.GlucosePercentageAbove:
          value = GlucosePercentageAbove
          break
        case InsightKey.GlucosePercentageBelow:
          value = GlucosePercentageBelow
          break
        case InsightKey.GlucosePercentageDistribution:
          value = GlucosePercentageDistribution
          break
        case InsightKey.GlucosePercentageWithin:
          value = GlucosePercentageWithin
          break
        case InsightKey.GlucoseQuartiles:
          value = GlucoseQuartiles
          break
        case InsightKey.GlucoseStandardDeviation:
          value = GlucoseStandardDeviation
          break
        case InsightKey.GlucoseTotalUnicorns:
          value = GlucoseTotalUnicorns
          break
        case InsightKey.InsulinAverage:
          value = InsulinAverage
          break
        case InsightKey.InsulinAverageBasal:
          value = InsulinAverageBasal
          break
        case InsightKey.InsulinAverageBolus:
          value = InsulinAverageBolus
          break
        case InsightKey.InsulinTotal:
          value = InsulinTotal
          break
        case InsightKey.InsulinTotalBasal:
          value = InsulinTotalBasal
          break
        case InsightKey.InsulinTotalBolus:
          value = InsulinTotalBolus
          break
        case InsightKey.LiquidAverage:
          value = LiquidAverage
          break
        case InsightKey.LiquidTotal:
          value = LiquidTotal
          break
        case InsightKey.MiniGraph:
          value = MiniGraph
          break
        case InsightKey.TimeInRange:
          value = TimeInRange
          break
      }

      return value as FC<
        TileProps<InsightLookupByKey<typeof insightKey>>
      > | null
    },
    [insightKey],
  )

  const range = insight.range ?? defaultInsightRangeByKey[insight.key]

  return TileComponent && (
    <TileComponent
      insightKey={insight.key}
      range={range}
    />
  )
}
